/**
 * Persistence + wording for learned format ceilings (see formatCeiling.ts).
 *
 * Cross-device truth: the zone profile in Supabase (`/api/zones/profiles`), stored under one
 * reserved `test_results` key so no schema change is needed. localStorage is only the instant
 * cache read synchronously at play time.
 */

import translations from '../i18n/translations';
import { detectOsLocale, resolveLanguage } from '../i18n/resolveLanguage';

export const LEARNED_CEILING_KEY = 'learned_format_ceiling';
const LOCAL_KEY = 'maestro_learned_format_ceilings';
/** Re-probe hi-res once a month so a firmware fix brings it back. */
export const CEILING_REPROBE_MS = 30 * 24 * 60 * 60 * 1000;

export type LearnedFormatCeiling = {
  max_format_id: number;
  from_format_id: number;
  learned_at: string;
  fingerprint: string;
  renderer_name: string;
  synced?: boolean;
};

type RendererDescription = {
  manufacturer?: string;
  model?: string;
  model_number?: string;
  modelNumber?: string;
  firmware_version?: string;
  software_version?: string;
};

/** Device-description fingerprint; a change (new firmware/model string) triggers a re-probe. */
export function rendererFingerprint(renderer: unknown): string {
  const r = (renderer || {}) as RendererDescription;
  return [r.manufacturer, r.model, r.model_number || r.modelNumber, r.firmware_version || r.software_version]
    .map((v) => String(v || '').trim())
    .filter(Boolean)
    .join('|');
}

export function isFormatCeilingStale(entry: LearnedFormatCeiling, currentFingerprint: string, now = Date.now()): boolean {
  const learnedAt = Date.parse(entry.learned_at);
  if (!Number.isFinite(learnedAt) || now - learnedAt > CEILING_REPROBE_MS) return true;
  return !!entry.fingerprint && !!currentFingerprint && entry.fingerprint !== currentFingerprint;
}

function isCeiling(v: unknown): v is LearnedFormatCeiling {
  const o = v as LearnedFormatCeiling | null;
  return !!o && typeof o === 'object' && typeof o.max_format_id === 'number' && typeof o.learned_at === 'string';
}

export function loadLocalCeilings(): Record<string, LearnedFormatCeiling> {
  try {
    const raw = JSON.parse(localStorage.getItem(LOCAL_KEY) || '{}');
    const out: Record<string, LearnedFormatCeiling> = {};
    for (const [id, v] of Object.entries(raw || {})) if (isCeiling(v)) out[id] = v;
    return out;
  } catch {
    return {};
  }
}

export function saveLocalCeilings(map: Record<string, LearnedFormatCeiling>): void {
  try { localStorage.setItem(LOCAL_KEY, JSON.stringify(map)); } catch { /* private mode */ }
}

export function readProfileCeiling(
  profile: { test_results?: Record<string, unknown> | null } | null | undefined,
): LearnedFormatCeiling | null {
  const raw = profile?.test_results?.[LEARNED_CEILING_KEY];
  return isCeiling(raw) ? raw : null;
}

/** Server wins: profiles carrying a ceiling set it; a synced local entry the server lost is dropped. */
export function mergeProfileCeilings(
  local: Record<string, LearnedFormatCeiling>,
  zones: Array<{ zone_id: string; test_results?: Record<string, unknown> | null }>,
): Record<string, LearnedFormatCeiling> {
  const next = { ...local };
  for (const z of zones) {
    if (!z?.zone_id) continue;
    const server = readProfileCeiling(z);
    if (server) next[z.zone_id] = { ...server, synced: true };
    else if (next[z.zone_id]?.synced) delete next[z.zone_id];
  }
  return next;
}

async function writeProfileCeiling(zoneId: string, entry: LearnedFormatCeiling | null): Promise<boolean> {
  const { useZoneProfileStore } = await import('../store/zoneProfileStore');
  const zone = useZoneProfileStore.getState().zones.find((z) => z.zone_id === zoneId);
  if (!zone) return false;
  const results: Record<string, unknown> = { ...(zone.test_results || {}) };
  if (entry) results[LEARNED_CEILING_KEY] = { ...entry, synced: undefined, status: 'learned', tested_at: entry.learned_at };
  else delete results[LEARNED_CEILING_KEY];
  await useZoneProfileStore.getState().updateZone(zoneId, { test_results: results as never });
  return true;
}

/** Best effort: the local cache already holds the ceiling; the profile write makes it cross-device. */
export async function persistLearnedCeiling(zoneId: string, _name: string, entry: LearnedFormatCeiling): Promise<void> {
  try {
    if (await writeProfileCeiling(zoneId, entry)) {
      const map = loadLocalCeilings();
      if (map[zoneId]) saveLocalCeilings({ ...map, [zoneId]: { ...map[zoneId], synced: true } });
    }
  } catch { /* stays local; re-sent on next learn */ }
}

export async function removePersistedCeiling(zoneId: string, _name: string): Promise<void> {
  try { await writeProfileCeiling(zoneId, null); } catch { /* local already cleared */ }
}

// ---------------------------------------------------------------------------
// Wording (all languages via translations.js → formatCeilingI18n)
// ---------------------------------------------------------------------------

function t(key: string, vars?: Record<string, string>): string {
  let lang = 'en';
  try { lang = resolveLanguage(localStorage.getItem('maestro_language'), detectOsLocale()); } catch { /* default */ }
  const catalog = translations as Record<string, Record<string, string>>;
  let str = catalog[lang]?.[key] || catalog.en?.[key] || key;
  for (const [k, v] of Object.entries(vars || {})) str = str.split(`{${k}}`).join(v);
  return str;
}

/** Connector evidence markers (`[format_ceiling_suspect …]`, `[no_renderer_fetch]`) are never shown. */
export function stripFormatCeilingMarkers(msg: string): string {
  return String(msg || '').replace(/\s*\[(?:format_ceiling_suspect|no_renderer_fetch)[^\]]*\]/g, '');
}

export function formatCeilingToast(rendererName: string | undefined, maxFormatId: number): string {
  const device = String(rendererName || '').trim() || t('format_ceiling_device_fallback');
  return t(maxFormatId === 6 ? 'format_ceiling_replay_cd' : 'format_ceiling_replay_96k', { device });
}

export function formatCeilingLabel(maxFormatId: number): string {
  return t(maxFormatId === 6 ? 'format_ceiling_learned_cd' : 'format_ceiling_learned_96k');
}
